import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, MapPin, TrendingUp, Car, Phone, Mail, 
  Calendar, RefreshCw, Star, ChevronDown, Activity, User,
  DollarSign, Award, History, Truck, FileText, Shield, Edit,
  Ban, CheckCircle, AlertTriangle, XCircle, Wallet, CreditCard,
  Briefcase, IdCard, Heart, Clock4, Route, Euro, Banknote,
  Key, Upload, Camera, Trash2, Eye, Download, RotateCcw
} from 'lucide-react';
import api from '../../services/api';
import { format, parseISO, differenceInDays } from 'date-fns';
import { sq } from 'date-fns/locale';
import { toast } from 'sonner';
import { useSyncManager } from '../../context/SyncContext';
import { formatCurrency, formatEuro, lekToEuro } from '../../utils/formatters';
import { parseUtcDate } from '../../utils/dateFormat';

const DriverProfilePage = () => {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const { getDriverStatus, isDriverOnline, fetchSnapshot } = useSyncManager();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [driverSnapshot, setDriverSnapshot] = useState(null);
  const [period, setPeriod] = useState('month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [shiftsData, setShiftsData] = useState([]);
  const [ridesFilter, setRidesFilter] = useState({ payment: 'all', date: '' });
  const [showResetModal, setShowResetModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch driver snapshot for real-time status
  const fetchDriverSnapshot = useCallback(async () => {
    try {
      const data = await fetchSnapshot(`/api/snapshot/driver-profile/${driverId}`);
      if (data) {
        setDriverSnapshot(data);
      }
    } catch (e) {
      console.log('Snapshot fetch error:', e);
    }
  }, [driverId, fetchSnapshot]);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const params = { period };
      if (period === 'custom' && customDateFrom && customDateTo) {
        params.date_from = customDateFrom;
        params.date_to = customDateTo;
      }
      
      const response = await api.get(`/performance/drivers/${driverId}`, { params });
      setProfile(response.data);
      
      // Also fetch snapshot for real-time status
      await fetchDriverSnapshot();
      
      try {
        const shiftsResp = await api.get(`/drivers/${driverId}/shifts`, { params: { limit: 30 } });
        setShiftsData(shiftsResp.data || []);
      } catch (e) {
        console.log('Shifts endpoint not available');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Gabim gjatë ngarkimit të profilit');
    } finally {
      setLoading(false);
    }
  }, [driverId, period, customDateFrom, customDateTo]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy, HH:mm', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const formatHours = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} orë`;
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today': return 'Sot';
      case 'week': return 'Kjo Javë';
      case 'month': return 'Ky Muaj';
      case 'year': return 'Ky Vit';
      case 'custom': return 'Periudhë';
      default: return 'Ky Muaj';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      online: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', label: 'Online' },
      offline: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400', label: 'Offline' },
      busy: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500', label: 'Në udhëtim' },
      suspended: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Suspenduar' },
      vacation: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', label: 'Pushim' }
    };
    const badge = badges[status] || badges.offline;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <span className={`w-2 h-2 rounded-full ${badge.dot} ${status === 'online' ? 'animate-pulse' : ''}`} />
        {badge.label}
      </span>
    );
  };

  const getDocumentStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', label: 'Pa datë', color: 'text-gray-500', bg: 'bg-gray-100' };
    const daysLeft = differenceInDays(new Date(expiryDate), new Date());
    if (daysLeft < 0) return { status: 'expired', label: 'Skaduar', color: 'text-red-600', bg: 'bg-red-100' };
    if (daysLeft <= 30) return { status: 'expiring', label: `Skadon për ${daysLeft} ditë`, color: 'text-amber-600', bg: 'bg-amber-100' };
    return { status: 'valid', label: 'Valide', color: 'text-green-600', bg: 'bg-green-100' };
  };

  const handleSuspend = async () => {
    if (!window.confirm('Jeni i sigurt që doni të suspendoni këtë shofer?')) return;
    try {
      await api.post(`/drivers/${driverId}/suspend`);
      toast.success('Shoferi u suspendua');
      fetchProfile();
    } catch (e) {
      toast.error('Gabim gjatë suspendimit');
    }
  };

  const handleActivate = async () => {
    try {
      await api.post(`/drivers/${driverId}/activate`);
      toast.success('Shoferi u aktivizua');
      fetchProfile();
    } catch (e) {
      toast.error('Gabim gjatë aktivizimit');
    }
  };

  const handleResetPassword = async () => {
    try {
      await api.post(`/drivers/${driverId}/reset-password`);
      toast.success('Fjalëkalimi u rivendos në "driver123"');
      setShowResetModal(false);
    } catch (e) {
      toast.error('Gabim gjatë rivendosjes së fjalëkalimit');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'license');

    setUploadingDoc(true);
    try {
      await api.post(`/drivers/${driverId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Dokumenti u ngarkua me sukses');
      fetchProfile();
      setShowUploadModal(false);
    } catch (e) {
      toast.error('Gabim gjatë ngarkimit të dokumentit');
    } finally {
      setUploadingDoc(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
        <User className="w-12 h-12 mb-3" />
        <p>Shoferi nuk u gjet</p>
      </div>
    );
  }

  const { driver, performance_summary, charts, ride_history, vehicle_history, today_sessions, is_online, current_session } = profile;

  // Get real-time status from snapshot (source of truth)
  const realStatus = driverSnapshot?.driver?.status || (is_online ? 'online' : 'offline');
  const isReallyOnline = driverSnapshot?.driver?.status === 'online' || isDriverOnline(driverId);

  const driverExtended = {
    ...driver,
    id_number: driver.id_number || 'I12345678A',
    birth_date: driver.birth_date || '1985-03-15',
    contract_status: driver.contract_status || 'active',
    employment_date: driver.employment_date || driver.created_at,
    documents: driver.documents || {
      license: { number: 'AL-12345', expiry: '2026-08-15', file_url: null }
    }
  };

  const filteredRides = (ride_history || []).filter(ride => {
    if (ridesFilter.payment !== 'all' && ride.payment_method !== ridesFilter.payment) return false;
    if (ridesFilter.date && !ride.created_at?.startsWith(ridesFilter.date)) return false;
    return true;
  });

  // KPI Stats
  const kpiStats = [
    { 
      label: 'Udhëtime', 
      value: performance_summary?.total_rides || 0, 
      icon: Route, 
      color: 'text-blue-600', 
      bg: 'bg-blue-50' 
    },
    { 
      label: 'Orë Pune', 
      value: formatHours(performance_summary?.total_minutes || 0), 
      icon: Clock, 
      color: 'text-purple-600', 
      bg: 'bg-purple-50' 
    },
    { 
      label: 'Vlerësimi', 
      value: (driver.rating || 5.0).toFixed(1), 
      icon: Star, 
      color: 'text-yellow-600', 
      bg: 'bg-yellow-50' 
    },
    { 
      label: 'Xhiro (LEK)', 
      value: formatCurrency((performance_summary?.total_earnings || 0) * 100), 
      subValue: formatEuro(lekToEuro((performance_summary?.total_earnings || 0) * 100)),
      icon: Wallet, 
      color: 'text-green-600', 
      bg: 'bg-green-50' 
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Profili i Shoferit</h1>
          <p className="text-sm text-gray-500">Informacion i plotë dhe statistika</p>
        </div>
        <button 
          onClick={fetchProfile}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </button>
      </div>

      {/* ===================== HEADER CARD WITH KPIs ===================== */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6">
          {/* Top Row: Avatar, Name, Status + Action Buttons */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg border-2 border-white/20">
                {driver.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'SH'}
              </div>
              
              {/* Name & Status - Using real-time status */}
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-white">{driver.name}</h2>
                  {getStatusBadge(realStatus)}
                </div>
                {driverSnapshot?.driver?.last_seen && (
                  <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Parë së fundmi: {formatDateTime(driverSnapshot.driver.last_seen)}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - Horizontal on right */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/admin/drivers/${driverId}/edit`)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition"
              >
                <Edit className="w-4 h-4" />
                Ndrysho
              </button>
              <button 
                onClick={() => setShowResetModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition"
              >
                <Key className="w-4 h-4" />
                Reseto Password
              </button>
              {driver.status === 'suspended' ? (
                <button 
                  onClick={handleActivate}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition"
                >
                  <CheckCircle className="w-4 h-4" />
                  Aktivizo
                </button>
              ) : (
                <button 
                  onClick={handleSuspend}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition"
                >
                  <Ban className="w-4 h-4" />
                  Suspendo
                </button>
              )}
            </div>
          </div>

          {/* KPI Cards - Full Width */}
          <div className="grid grid-cols-4 gap-4">
            {kpiStats.map((stat, idx) => (
              <div key={idx} className={`${stat.bg} rounded-xl p-4 flex items-center gap-4`}>
                <div className={`w-12 h-12 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===================== INFO CARDS ROW ===================== */}
      <div className="grid grid-cols-3 gap-4">
        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            Informacion Bazë
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" /> Telefon
              </span>
              <span className="font-medium">{driver.phone}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email
              </span>
              <span className="font-medium text-sm">{driver.email || '--'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm flex items-center gap-2">
                <IdCard className="w-4 h-4" /> Nr. ID
              </span>
              <span className="font-medium">{driverExtended.id_number}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Datëlindje
              </span>
              <span className="font-medium">{formatDate(driverExtended.birth_date)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Punësuar
              </span>
              <span className="font-medium">{formatDate(driverExtended.employment_date)}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-gray-500 text-sm flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Kontratë
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                driverExtended.contract_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {driverExtended.contract_status === 'active' ? 'Aktive' : 'Joaktive'}
              </span>
            </div>
          </div>
        </div>

        {/* Live Operative Status */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Status Operativ (Live)
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Statusi</span>
              {getStatusBadge(realStatus)}
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Automjeti Aktual</span>
              <span className="font-medium flex items-center gap-2">
                {driverSnapshot?.current_vehicle?.plate || current_session?.vehicle_plate ? (
                  <>
                    <Car className="w-4 h-4 text-red-500" />
                    <span className="px-2 py-1 bg-red-50 text-red-700 rounded font-mono font-bold">
                      {driverSnapshot?.current_vehicle?.plate || current_session?.vehicle_plate}
                    </span>
                  </>
                ) : (
                  <>
                    <Car className="w-4 h-4 text-gray-400" />
                    Pa automjet
                  </>
                )}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Udhëtime në Sesion</span>
              <span className="font-bold text-blue-600">
                {driverSnapshot?.stats?.trips_today || current_session?.trips_count || 0}
              </span>
            </div>
            
            {isReallyOnline && (driverSnapshot?.current_shift || current_session?.started_at) && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-700 text-sm font-medium">Sesioni Aktual</span>
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded animate-pulse">
                    Live
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-green-600">Filloi</p>
                    <p className="font-bold text-green-800">{formatDateTime(current_session.started_at)}</p>
                  </div>
                  <div className="h-8 w-px bg-green-200"></div>
                  <div>
                    <p className="text-xs text-green-600">Kohëzgjatja</p>
                    <p className="font-bold text-green-800">
                      {(() => {
                        const started = parseUtcDate(current_session.started_at);
                        const now = new Date();
                        const diffMs = now - started;
                        const hours = Math.floor(diffMs / 3600000);
                        const mins = Math.floor((diffMs % 3600000) / 60000);
                        return hours > 0 ? `${hours}h ${mins}m` : `${mins} min`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Udhëtime në Sesion</span>
              <span className="font-bold text-blue-600">
                {current_session?.rides_count || today_sessions?.reduce((sum, s) => sum + (s.rides_count || 0), 0) || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Documents - Only Patentë with Upload */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Dokumentet
            </h3>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
            >
              <Upload className="w-4 h-4" />
              Ngarko
            </button>
          </div>
          
          {/* License Document */}
          <div className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                  <IdCard className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Patentë</p>
                  <p className="text-sm text-gray-500">
                    Nr: {driverExtended.documents?.license?.number || '--'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Skadon: {formatDate(driverExtended.documents?.license?.expiry)}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {(() => {
                  const status = getDocumentStatus(driverExtended.documents?.license?.expiry);
                  return (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${status.bg} ${status.color}`}>
                      {status.label}
                    </span>
                  );
                })()}
                {driverExtended.documents?.license?.file_url && (
                  <div className="flex gap-1">
                    <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Upload area if no file */}
            {!driverExtended.documents?.license?.file_url && (
              <div className="mt-3 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-red-300 transition cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Kliko për të ngarkuar patentën</p>
                <p className="text-xs text-gray-400">PDF, JPG ose PNG deri në 5MB</p>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* ===================== TABS & CONTENT ===================== */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Period Filter & Tabs */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-4">
          <span className="text-sm text-gray-500">Periudha:</span>
          
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <Calendar className="w-4 h-4 text-gray-500" />
              <span>{getPeriodLabel()}</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            
            {showDatePicker && (
              <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-72 p-4">
                <div className="space-y-2">
                  {[
                    { key: 'today', label: 'Sot' },
                    { key: 'week', label: 'Kjo Javë' },
                    { key: 'month', label: 'Ky Muaj' },
                    { key: 'year', label: 'Ky Vit' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setPeriod(opt.key); setShowDatePicker(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition ${
                        period === opt.key ? 'bg-red-50 text-red-600 font-medium' : 'hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Periudhë e Personalizuar</p>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Nga data</label>
                        <input
                          type="date"
                          value={customDateFrom}
                          onChange={(e) => setCustomDateFrom(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Deri në datë</label>
                        <input
                          type="date"
                          value={customDateTo}
                          onChange={(e) => setCustomDateTo(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => { setPeriod('custom'); setShowDatePicker(false); }}
                      disabled={!customDateFrom || !customDateTo}
                      className="w-full mt-3 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Apliko
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-auto">
            {[
              { key: 'overview', label: 'Statistika', icon: TrendingUp },
              { key: 'shifts', label: 'Turnet', icon: Clock4 },
              { key: 'rides', label: 'Udhëtimet', icon: Route },
              { key: 'vehicles', label: 'Automjetet', icon: Car },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="text-center py-12 text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Statistikat do të shfaqen këtu</p>
            </div>
          )}

          {activeTab === 'shifts' && (
            <div className="text-center py-12 text-gray-500">
              <Clock4 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Turnet do të shfaqen këtu</p>
            </div>
          )}

          {activeTab === 'rides' && (
            <div className="text-center py-12 text-gray-500">
              <Route className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Udhëtimet do të shfaqen këtu</p>
            </div>
          )}

          {activeTab === 'vehicles' && (
            <div className="text-center py-12 text-gray-500">
              <Car className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Historia e automjeteve do të shfaqet këtu</p>
            </div>
          )}
        </div>
      </div>

      {/* ===================== RESET PASSWORD MODAL ===================== */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Key className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Reseto Fjalëkalimin</h3>
                <p className="text-sm text-gray-500">{driver.name}</p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                Fjalëkalimi i shoferit do të rivendoset në: <strong>driver123</strong>
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Shoferi duhet ta ndryshojë pas hyrjes së parë.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Anulo
              </button>
              <button
                onClick={handleResetPassword}
                className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reseto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== UPLOAD DOCUMENT MODAL ===================== */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Ngarko Dokument</h3>
              <button 
                onClick={() => setShowUploadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div 
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-red-400 transition cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadingDoc ? (
                <RefreshCw className="w-10 h-10 text-red-500 mx-auto mb-3 animate-spin" />
              ) : (
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              )}
              <p className="text-gray-600 font-medium">
                {uploadingDoc ? 'Duke ngarkuar...' : 'Kliko ose tërhiq dokumentin këtu'}
              </p>
              <p className="text-sm text-gray-400 mt-1">PDF, JPG ose PNG deri në 5MB</p>
            </div>
            
            <div className="mt-4 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Lloji i dokumentit</label>
              <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
                <option value="license">Patentë</option>
              </select>
            </div>
            
            <div className="mt-4 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Data e skadimit</label>
              <input 
                type="date"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverProfilePage;
